import { DateTime } from 'luxon';
import { DatabaseUpdateParser, getFirebaseServices, getAuthenticatedUserInfo } from '../DatabaseUpdateParser.js';

/** @import { UpdateParserOverrides } from '../DatabaseUpdateParser.js' */

const SHARING_PROPAGATION_DELAY_MS = 5000;

/**
 * Get a parser that updates the last updated timestamp of a job
 * @param {UpdateParserOverrides?} [OVERRIDES] - Overrides for using this parser in different contexts (e.g. on the server with admin)
 * @returns {DatabaseUpdateParser} A job-last-updated parser
 */
export function getLastUpdatedParser(OVERRIDES = null) {
  // Stores the last time the user updated a job
  const lastUpdatedLookup = {};

  return DatabaseUpdateParser.fromConfig({
    globalPathFilter: /^photoheight\/jobs\/*./,
    eventTypes: [
      {
        name: 'LAST_UPDATED',
        pathMatch: /^photoheight\/jobs\/(?<jobId>[^\/\s]+)(?:\/.*)?$/,
        parse: async ({ jobId }) => ({ jobId }),
        onEventGroup: async (events) => {
          const { firestoreDB, realtimeDB, FieldValue } = getFirebaseServices(OVERRIDES);
          const { uid: userUid } = await getAuthenticatedUserInfo(OVERRIDES);
          // Get a list of all the job IDs that were updated
          const jobIds = [...new Set(events.map((event) => event.jobId))];
          // Get the current time in millis
          const currentMillis = DateTime.now().toMillis();

          // For each job ID, try to update the "last updated" time
          for (const jobId of jobIds) {
            // If we are calling this function more than once per minute, ignore the update
            if (currentMillis - (lastUpdatedLookup[jobId] || 0) < 60 * 1000) return;
            lastUpdatedLookup[jobId] = currentMillis;

            // Get the job owner for this job
            const jobOwner = (await realtimeDB.ref(`photoheight/jobs/${jobId}/job_owner`).once('value')).val();

            // Update the record in firestore
            const lastUpdatedRecord = {
              last_updated: FieldValue.serverTimestamp(),
              last_updated_by: userUid,
              ...(globalThis.isMobile && {
                mobile_last_updated: FieldValue.serverTimestamp(),
                mobile_last_updated_by: userUid
              })
            };

            await updateJobTimestamp(firestoreDB, jobId, jobOwner, lastUpdatedRecord).catch(async (error) => {
              // In the case of an error, we might be creating the job for the first time.
              // In that case, it will take some time for the sharing object to be
              // populated, so wait a bit and then try again.
              await new Promise((resolve) => setTimeout(resolve, SHARING_PROPAGATION_DELAY_MS));
              return updateJobTimestamp(firestoreDB, jobId, jobOwner, lastUpdatedRecord);
            });
          }
        }
      }
    ]
  });
}

/**
 * Update the job timestamp in both the company jobs_last_updated and the jobs collection
 * @param {import('firebase/compat/app').default.firestore.Firestore} firestoreDB - Firestore database instance
 * @param {string} jobId - Job ID
 * @param {string} jobOwner - Job owner (company ID)
 * @param {object} updateFields - Fields to update
 */
async function updateJobTimestamp(firestoreDB, jobId, jobOwner, updateFields) {
  const companyDoc = firestoreDB.doc(`companies/${jobOwner}/jobs_last_updated/${jobId}`);
  const jobDoc = firestoreDB.doc(`jobs/${jobId}`);
  const batch = firestoreDB.batch();
  batch.set(companyDoc, updateFields, { merge: true });
  batch.set(jobDoc, { metadata: updateFields }, { merge: true });
  await batch.commit();
}
